import {
  Alert,
  Drawer,
  Form,
  FormInstance,
  Image,
  Input,
  message,
  Row,
  Select,
  Skeleton,
  Steps
} from "antd";
import AppButton from "components/libs/button";
import React, { FC, useEffect, useState } from "react";


//!API
import * as TEACHER_SERVICES from "api/services/teacher/teacher.services";

//!IMAGES
import LogoClasses from "assets/images/teacher/teacher_classes.svg";
import ArrowBack from "../../assets/images/app/arrow_back.svg";

//!STYLE
import { TransferItem } from "antd/es/transfer";
import { StatusCode } from "common/enums";
import AppResult from "components/appResults";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TableTransfer from "screens/teacher/organisme/components/tableTransfert";
import { setNewInvitationSent } from "store/app/appSlice";
import s from "./style.module.css";

const { Step } = Steps;

// Define the shape of a student item
interface Student extends TransferItem {
  lastname: string;
  firstname: string,
  level: string;
  credits: number;
}

type Props = {
  addCourse?: (values: any) => void;
  showAddClasse: boolean;
  setShowAddClasse: React.Dispatch<React.SetStateAction<boolean>>;
  addCourseForm?: FormInstance<any>;
  addCourseLoading?: boolean;
  current: number;
  roomData: any;
};

const { Option } = Select;

const ParentOrganismAddRoom: FC<Props> = ({
  addCourse,
  showAddClasse,
  setShowAddClasse,
  addCourseForm,
  addCourseLoading,
  current,
  roomData
}) => {

  //!STATES
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState<boolean>(false);
  const [isInviting, setIsInviting] = useState<boolean>(false);
  const [addCourseError, setAddCourseError] = useState<string>("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [organismeStudents, setOrganismeStudents] = useState<any[]>([]);
  const [studentsList, setStudentsList] = useState<any[]>([]);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 767 && window.innerWidth <= 1024);

  const handleResize = () => {
    setIsMobile(window.innerWidth <= 767);
    setIsTablet(window.innerWidth > 767 && window.innerWidth <= 1024);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchStudentsList = async (organismeId: string) => {
    try {
      setIsDataLoading(true);
      const response = await TEACHER_SERVICES.getOrganismeStudents(organismeId);
      if (response?.statusCode === StatusCode.OK) {
        setOrganismeStudents(response?.data);
      }
    } catch (error) {
    }
    finally {
      setIsDataLoading(false);
    }
  };

  /* const inviteStudent = async (studentId: string) => {
    try {
      setIsInviting(true);

      const response = await TEACHER_SERVICES.sendInvitationToStudent(roomData?._id, studentId);

      if (response?.statusCode === StatusCode.CREATED) {
        message.success({
          content: "Invitation envoyée avec succès",
        });
        setShowAddClasse(false);
        dispatch(setNewInvitationSent(true));
      }
    } catch (error) {
      console.log(error);
    }
    finally {
      setIsInviting(false);
    }
  }; */

  const handleInviteStudents = async () => {
    try {
      setIsInviting(true);

      const results = await Promise.all(
        studentsList.map(async (student: any) => {
          try {
            const response = await TEACHER_SERVICES.addStudentToClassroom(
              roomData?._id,
              student?.key
            );

            if (response?.statusCode !== StatusCode.CREATED) {
              throw new Error("Failed");
            }

            return { success: true, studentId: student?.key };
          } catch (error) {
            return { success: false, studentId: student?.key };
          }
        })
      );

      const successCount = results.filter(r => r.success).length;
      const errors = results.filter(r => !r.success);

      if (successCount > 0) {
        message.success(successCount > 1 ? "Elèves ajoutés avec succès" : "Elève ajouté avec succès");
        setShowAddClasse(false);
        dispatch(setNewInvitationSent(true));
      }

      errors.forEach(() => {
        message.error("Erreur lors de l'ajout d'un élève");
      });

    } catch (error) {
      console.log(error);
    } finally {
      setIsInviting(false);
    }
  };

  /* const handleInviteStudents = async () => {
    studentsList.map((student: any) => {
      inviteStudent(student?.key);
    })
  } */

  // Sample data for students
  const students: Student[] = organismeStudents.map((student, index) => ({
    key: student?.id,
    firstname: student?.firstname,
    lastname: student?.lastname,
    level: student?.level,
    credits: student?.credits,
  }));

  const onChange = (nextTargetKeys: string[], direction: string, moveKeys: string[]) => {
    setTargetKeys(nextTargetKeys);

    // Find students that were moved to the right column
    if (direction === 'right') {
      const movedStudents = students.filter(student =>
        student.key && moveKeys.includes(student.key)  // Ensure student.key is defined
      );
      setStudentsList(prevStudents => [...prevStudents, ...movedStudents]);
    }

    // Handle students moved to the left column
    if (direction === 'left') {
      const removedStudents = students.filter(student =>
        student.key && moveKeys.includes(student.key)  // Ensure student.key is defined
      );
      setStudentsList(prevStudents =>
        prevStudents.filter(student => !removedStudents.some(s => s.key === student.key))
      );
    }
  };

  // Define columns for the transfer table - responsive widths
  const getColumnWidth = () => {
    if (isMobile) return 120;
    if (isTablet) return 180;
    return 250;
  };

  const leftColumns = [
    { dataIndex: 'lastname', title: 'Nom', width: getColumnWidth() },
    { dataIndex: 'firstname', title: 'Prénoms', width: getColumnWidth() },
    {
      dataIndex: 'level',
      title: 'Niveau',
      width: getColumnWidth(),
      render: (levelId: string) => {
        const backgroundColor = '#ebfefb';
        return (
          <div style={{ backgroundColor, padding: '4px 8px', borderRadius: '8px', fontSize: isMobile ? '0.8em' : '1em' }}>
            {levelId}
          </div>
        );
      }
    },
  ];

  const rightColumns = [
    { dataIndex: 'lastname', title: 'Nom', width: getColumnWidth() },
    { dataIndex: 'firstname', title: 'Prénoms', width: getColumnWidth() },
    {
      dataIndex: 'level',
      title: 'Niveau',
      width: getColumnWidth(),
      render: (levelId: string) => {
        const backgroundColor = '#ebfefb';
        return (
          <div style={{ backgroundColor, padding: '4px 8px', borderRadius: '8px', fontSize: isMobile ? '0.8em' : '1em' }}>
            {levelId}
          </div>
        );
      }
    },
  ];

  useEffect(() => {
    fetchStudentsList(user?._id);
  }, [user]);

  return (
    <Drawer
      className="drawer"
      title={false}
      placement="right"
      width={isMobile ? "100%" : isTablet ? "80%" : "65%"}
      onClose={() => setShowAddClasse!(!showAddClasse)}
      visible={showAddClasse}
      closable={true}
      headerStyle={{
        border: "none",
        paddingTop: isMobile ? "30px" : "50px",
        paddingLeft: isMobile ? "15px" : isTablet ? "30px" : "40px",
        backgroundColor: "transparent"
      }}
      closeIcon={
        <img
          src={ArrowBack}
          height={isMobile ? 10 : 18}
          alt="Back"
        />
      }
      drawerStyle={{
        backgroundColor: "#FFF",
        borderTopLeftRadius: isMobile ? "0px" : "50px",
        borderBottomLeftRadius: isMobile ? "0px" : "50px",
        boxShadow: "-3px 3px 34px #39393929"
      }}
      bodyStyle={{
        padding: isMobile ? "15px" : isTablet ? "20px" : "24px"
      }}
    >
      <div className={s.drawer}>
        <div className={s.drawer__content}>
          <div className={s.drawer___header}>
            <Image preview={false} src={LogoClasses} width={isMobile ? 60 : isTablet ? 70 : 80} />
            <h3>{current === 2 ? "Inviter des élèves" : "Créer une classe"}</h3>
          </div>

          {current === 1 && <Form
            name="create-room"
            labelCol={{ span: 7 }}
            wrapperCol={{ span: 17 }}
            autoComplete="off"
            onFinish={addCourse}
            form={addCourseForm}
          >
            <div className={s.fieldBox}>
              <div style={{
                width: "auto",
                fontSize: isMobile ? "0.85em" : "12px",
                textAlign: "left",
                marginRight: isMobile ? "0" : "20px"
              }}>
                <label htmlFor="title">Nom de la classe :</label>
              </div>
              <div className={s.field}>
                <Form.Item label="" name="title">
                  <Input
                    className={s.fieldInput}
                    bordered={false}
                    placeholder="6ème, Terminale"
                    style={{ fontSize: isMobile ? '0.9em' : '1em' }}
                  />
                </Form.Item>
              </div>
            </div>

            {addCourseError && (
              <Form.Item>
                <Alert
                  message="Une erreur est survenue !"
                  description={addCourseError}
                  type="error"
                  closable
                />
              </Form.Item>
            )}

            <Row justify="center">
              <AppButton htmlTypeSubmit={true} loading={addCourseLoading} className={s.newButton}>
                valider
              </AppButton>
            </Row>
          </Form>}

          {current === 2 && isDataLoading ? <Skeleton /> : current === 2 && <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "auto",
            paddingBottom: isMobile ? "20px" : "5%",
            borderRadius: "12px",
            marginBottom: isMobile ? "40px" : "100px",
            width: "100%",
            overflowX: isMobile ? "hidden" : "visible"
          }}>
            {organismeStudents.length !== 0 && <div style={{
              fontSize: isMobile ? "14px" : "16px",
              color: "#000",
              marginBottom: isMobile ? "15px" : "20px",
              textAlign: "center",
              padding: isMobile ? "0 10px" : "0"
            }}>
              Sélectionner les étudiants
            </div>}
            {organismeStudents.length === 0 ?
              <AppResult status={"info"} title={"Aucun élève trouvé dans ta recherche. Choisis un organisme."} /> :
              <div style={{
                width: "100%",
                overflowX: isMobile ? "auto" : "visible",
                padding: isMobile ? "0" : "0 10px"
              }}>
                <TableTransfer
                  dataSource={students}
                  targetKeys={targetKeys}
                  onChange={onChange}
                  filterOption={(inputValue, item) =>
                    item.lastname.toLowerCase().includes(inputValue.toLowerCase()) ||
                    item.firstname.toLowerCase().includes(inputValue.toLowerCase()) ||
                    item.level.toLowerCase().includes(inputValue.toLowerCase())
                  }
                  leftColumns={leftColumns}
                  rightColumns={rightColumns}
                  showSearch={true}
                />
              </div>
            }
            {studentsList.length !== 0 && <div style={{
              marginTop: isMobile ? "20px" : "40px",
              width: "100%",
              display: "flex",
              flexDirection: "row",
              alignItems: isMobile ? "center" : "flex-end",
              justifyContent: isMobile ? "center" : "flex-end",
              padding: isMobile ? "0 10px" : "0"
            }}>
              <AppButton
                disabled={isInviting}
                style={{
                  width: isMobile ? "100% !important" : "150px !important",
                }}
                onClick={handleInviteStudents}
              >
                SUIVANT
              </AppButton>
            </div>}
          </div>}
        </div>
      </div>
    </Drawer >
  );
};

export default ParentOrganismAddRoom;